import React, { useCallback, useId, useMemo } from "react";
import Multiselect from "react-widgets/Multiselect";
import MultiselectTag, { MultiselectTagProps } from "react-widgets/MultiselectTag";

export interface SpMultiSelectOption<Id extends string> {
    id: Id;
    label: string;
}

interface SpMultiSelectProps<Id extends string> {
    options: SpMultiSelectOption<Id>[];
    value: Id[];
    onChange: (ids: Id[]) => void;
    label?: string;
    className?: string;
    placeholder?: string;
    disabled?: boolean;
}

export function SpMultiSelect<Id extends string>(
    props: SpMultiSelectProps<Id>,
): React.ReactElement {
    const { options, value, onChange, label, className, placeholder, disabled } = props;
    const id = useId();

    const filteredValue = useMemo(() => {
        const availableIds = new Set(options.map(option => option.id));
        const selectedIds = value.filter(id => availableIds.has(id));
        const optionsById = new Map(options.map(option => [option.id, option]));
        return selectedIds
            .map(id => optionsById.get(id))
            .filter((option): option is SpMultiSelectOption<Id> => !!option);
    }, [options, value]);

    const handleChange = useCallback(
        (selected: SpMultiSelectOption<Id>[]) => {
            onChange(selected.map(option => option.id));
        },
        [onChange],
    );

    const tagOptionComponent = useCallback(
        (tagProps: MultiselectTagProps): React.ReactElement => {
            const { onRemove, ...rest } = tagProps;
            const handleRemove = (
                dataItem: MultiselectTagProps["dataItem"],
                event: React.MouseEvent<HTMLButtonElement>,
            ): void => {
                event.stopPropagation();
                onRemove(dataItem, event);
            };
            return <MultiselectTag {...rest} onRemove={handleRemove} />;
        },
        [],
    );

    const select = (
        <Multiselect
            id={id}
            data={options}
            value={filteredValue}
            dataKey="id"
            textField="label"
            placeholder={placeholder}
            disabled={disabled || options.length === 0}
            onChange={handleChange}
            tagOptionComponent={tagOptionComponent}
        />
    );

    return (
        <div className={`${className ?? ""} w-full`}>
            {label ? (
                <div>
                    <label htmlFor={`${id}_input`} className="mb-1 block">
                        {label}
                    </label>
                    {select}
                </div>
            ) : (
                select
            )}
        </div>
    );
}
